import AbstractCustomerValueModel from '../abstract_customer_value_model';
import BenefitsChart from '../../charts/benefits_chart';
import BenefitsChartValue from '../../charts/benefits_chart_value';
import BenefitsWaterfallChart from '../../charts/benefits_waterfall_chart';

class EnfosRoi extends AbstractCustomerValueModel {
  custom_chart_classes() {
    return { BenefitsChart, BenefitsChartValue, BenefitsWaterfallChart };
  }

  // Override this method in a child class to change the color of the total value dollar amount in benefit
  // waterfall charts. Since it is overlaid on top of the total value bar, it can be hard to read unless it
  // is set to a contrasting color.
  chart_waterfall_total_value_color() {
    return '#000000';
  }

  // Override this method in a child class to change the color of the individual value dollar amounts in benefit
  // waterfall charts. Since it is overlaid on top of the total value bar, it can be hard to read unless it
  // is set to a contrasting color.
  chart_waterfall_value_color() {
    return '#1E315D';
  }

  pct_rdxn_admin_burden() {
    return this.input.pct_rdxn_admin_burden / 100;
  }

  pct_rdxn_time_managing() {
    return this.input.pct_rdxn_time_managing / 100;
  }

  pct_rdxn_time_responding() {
    return this.input.pct_rdxn_time_responding / 100;
  }

  pct_rdxn_overages() {
    return this.input.pct_rdxn_overages / 100;
  }

  pct_rdxn_overforecasting() {
    return this.input.pct_rdxn_overforecasting / 100;
  }

  pct_incr_cash_efficiency() {
    return this.input.pct_incr_cash_efficiency / 100;
  }

  adoption_rate_year_1() {
    return this.input.adoption_rate_year_1 / 100;
  }

  adoption_rate_year_2() {
    return this.input.adoption_rate_year_2 / 100;
  }

  adoption_rate_year_3() {
    return this.input.adoption_rate_year_3 / 100;
  }

  adoption_rate(year) {
    switch (year) {
    case 1: return this.adoption_rate_year_1();
    case 2: return this.adoption_rate_year_2();
    case 3: return this.adoption_rate_year_3();
    default: return 1.0;
    }
  }

  annual_ops_and_finance_salary() {
    return this.input.annual_ops_and_finance_salary;
  }

  annual_pm_salary() {
    return this.input.annual_pm_salary;
  }

  annual_price_increase(year) {
    return (1 + (this.input.annual_price_increase / 100)) ** (year - 1);
  }

  burden_rate() {
    return this.input.burden_rate;
  }

  end_of_fy_ero() {
    return this.input.end_of_fy_ero;
  }

  ero_planning_fee() {
    return this.prospect_saas_price()
      * 0.35;
  }

  ero_spend() {
    return this.input.ero_spend;
  }

  fully_burdened_hourly_rate(salary) {
    return salary
      * this.burden_rate()
      / 8
      / 260;
  }

  hr_managing_audits() {
    return this.input.hr_managing_audits;
  }

  hr_managing_budget() {
    return this.input.hr_managing_budget;
  }

  hr_managing_spend() {
    return this.input.hr_managing_spend;
  }

  inventory_mgmt_fee() {
    return this.prospect_saas_price()
      * 0.05; // "Solution Value Driver" F6
  }

  pct_discount() {
    return this.input.pct_discount / 100;
  }

  pct_overage() {
    return this.input.pct_overage / 100;
  }

  pct_overforecast() {
    return this.input.pct_overforecast / 100;
  }

  pct_roic() {
    return 5 / 100;
  }

  pct_saas_factor() {
    if (this.ero_spend() <= 5000000) { return 0.025; }
    if (this.ero_spend() <= 10000000) { return 0.02; }
    if (this.ero_spend() <= 25000000) { return 0.014; }
    if (this.ero_spend() <= 50000000) { return 0.01; }
    if (this.ero_spend() <= 50000000) { return 0.01; }
    if (this.ero_spend() <= 100000000) { return 0.0075; }

    return 0.006;
  }

  pct_saas() {
    const base_price_buffer = 0.2;

    return (this.pct_saas_factor()
              * (1 + base_price_buffer))
      * (1 - this.pct_discount());
  }

  prospect_saas_price() {
    const es = this.ero_spend();

    if (es < 1000000) { return 36000; }
    if (es < 10000000) { return 36000 + (es - 100000) * 1.66 / 100; }
    if (es < 25000000) { return 185400 + (es - 10000000) * 1.3 / 100; }
    if (es < 50000000) { return 380400 + (es - 25000000) * 1.07 / 100; }
    if (es < 100000000) { return 647900 + (es - 50000000) * 0.85 / 100; }
    if (es < 200000000) { return 1072900 + (es - 100000000) * 0.65 / 100; }
    if (es < 500000000) { return 1722900 + (es - 200000000) * 0.41 / 100; }

    return 2952900 + (es - 500000000) * 0.24;
  }

  saas_fee() {
    if (this.input.saas_fee_edited === 'true') {
      return this.input.saas_fee;
    }

    return this.inventory_mgmt_fee()
      + this.ero_planning_fee()
      + this.spend_mgmt_fee();
  }

  spend_mgmt_fee() {
    return this.prospect_saas_price()
      * 0.28;
  }

  start_of_fy_ero() {
    return this.input.start_of_fy_ero;
  }

  // Benefit 1
  reduce_admin_burden() {
    return this.hr_managing_spend()
      * 12
      * this.fully_burdened_hourly_rate(this.annual_pm_salary())
      * this.pct_rdxn_admin_burden();
  }

  // Benefit 2
  reduce_time_managing() {
    return this.hr_managing_budget()
      * 12
      * this.fully_burdened_hourly_rate(this.annual_ops_and_finance_salary())
      * this.pct_rdxn_time_managing();
  }

  // Benefit 3
  reduce_time_responding() {
    return this.hr_managing_audits()
      * this.fully_burdened_hourly_rate(this.annual_pm_salary())
      * this.pct_rdxn_time_responding();
  }

  // Benefit 4
  reduce_overages() {
    return this.ero_spend()
      * this.pct_overage()
      * 0.10
      * this.pct_rdxn_overages();
  }

  // Benefit 5
  reduce_overforecasting() {
    return this.ero_spend()
      * this.pct_overforecast()
      * this.pct_roic()
      * this.pct_rdxn_overforecasting();
  }

  // Benefit 6
  improve_cash_efficiency() {
    const adjustment = this.end_of_fy_ero() - (this.start_of_fy_ero() - this.ero_spend());
    const cash_efficiency = (this.start_of_fy_ero() - this.end_of_fy_ero()) / this.ero_spend();
    const post = adjustment
      * (1 - this.pct_incr_cash_efficiency());

    return (adjustment - post) * this.pct_roic();
  }

  all_benefits() {
    return [
      'reduce_admin_burden',
      'reduce_time_managing',
      'reduce_time_responding',
      'reduce_overages',
      'reduce_overforecasting',
      'improve_cash_efficiency'
    ];
  }

  value_drivers() {
    return [
      'increase',
      'optimize',
      'mitigate'
    ];
  }

  increase() {
    return this.reduce_admin_burden()
      + this.reduce_time_managing()
      + this.reduce_time_responding();
  }

  optimize() {
    return this.reduce_overages()
      + this.reduce_overforecasting();
  }

  mitigate() {
    return this.improve_cash_efficiency();
  }

  total_annual_investment(year = 1) {
    return this.saas_fee()
      * this.annual_price_increase(year);
  }

  increase_label() {
    return this.input.increase_label;
  }

  optimize_label() {
    return this.input.optimize_label;
  }

  mitigate_label() {
    return this.input.mitigate_label;
  }

  reduce_admin_burden_label() {
    return this.input.reduce_admin_burden_label;
  }

  reduce_time_managing_label() {
    return this.input.reduce_time_managing_label;
  }

  reduce_time_responding_label() {
    return this.input.reduce_time_responding_label;
  }

  reduce_overages_label() {
    return this.input.reduce_overages_label;
  }

  reduce_overforecasting_label() {
    return this.input.reduce_overforecasting_label;
  }

  improve_cash_efficiency_label() {
    return this.input.improve_cash_efficiency_label;
  }
}

export default EnfosRoi;